import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { Model, RegistryConfigResponse, RegistryStatsResponse } from '../../types';
import RegistryEntriesList from './RegistryEntriesList';

interface ModelRegistryTabProps {
  model: Model;
  initialSubTab?: 'configuration' | 'entries';
  onRegistryChange?: () => void;
}

type SubTab = 'configuration' | 'entries';

/**
 * ModelRegistryTab - Configuration and Entries UI for Model Output Registry
 *
 * This component provides a tabbed interface for:
 * - Configuration: Enable/disable registry, configure dedup fields
 * - Entries: Browse and search registered entries
 */
const ModelRegistryTab: React.FC<ModelRegistryTabProps> = ({ model, initialSubTab, onRegistryChange }) => {
  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState<SubTab>(initialSubTab || 'configuration');

  // Update activeSubTab when initialSubTab prop changes
  useEffect(() => {
    if (initialSubTab) {
      setActiveSubTab(initialSubTab);
    }
  }, [initialSubTab]);

  // State for configuration
  const [config, setConfig] = useState<RegistryConfigResponse | null>(null);
  const [stats, setStats] = useState<RegistryStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Local form state
  const [enabled, setEnabled] = useState(false);
  const [dedupMode, setDedupMode] = useState<'entire' | 'specific'>('entire');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  // Get model properties (fields)
  const modelFields = model.properties ? Object.keys(model.properties) : [];

  // Load configuration and stats on mount
  useEffect(() => {
    loadData();
  }, [model.model_id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load config and stats in parallel
      const [configData, statsData] = await Promise.all([
        apiService.getRegistryConfig(model.model_id),
        apiService.getRegistryStats(model.model_id).catch(() => null) // Stats may fail if registry not enabled
      ]);

      setConfig(configData);
      setStats(statsData);
      setEnabled(configData.registry_enabled);

      // Determine dedup mode from config
      if (configData.registry_dedup_fields !== null) {
        setDedupMode('specific');
        setSelectedFields(configData.registry_dedup_fields);
      } else {
        setDedupMode('entire');
        setSelectedFields([]);
      }
    } catch (err) {
      setError('Error loading registry configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = () => {
    setEnabled(!enabled);
  };

  const handleDedupModeChange = (mode: 'entire' | 'specific') => {
    setDedupMode(mode);
    if (mode === 'entire') {
      setSelectedFields([]);
    }
  };

  const handleFieldToggle = (field: string) => {
    setSelectedFields(prev => {
      if (prev.includes(field)) {
        return prev.filter(f => f !== field);
      }
      return [...prev, field];
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      const updatePayload = {
        registry_enabled: enabled,
        registry_dedup_fields: dedupMode === 'specific' ? selectedFields : null
      };

      const updatedConfig = await apiService.updateRegistryConfig(model.model_id, updatePayload);
      setConfig(updatedConfig);
      setSuccessMessage('Configuration saved successfully');

      // Refresh stats after save
      if (enabled) {
        const statsData = await apiService.getRegistryStats(model.model_id).catch(() => null);
        setStats(statsData);
      }

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('An error occurred while saving. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const buildPreviewText = (): string => {
    if (dedupMode === 'entire') {
      const fieldPreview = modelFields.map(f => `"${f}": <value>`).join(', ');
      return `{ ${fieldPreview} }`;
    } else {
      const fieldPreview = selectedFields.map(f => `"${f}": <value>`).join(', ');
      return selectedFields.length > 0 ? `{ ${fieldPreview} }` : '{ }';
    }
  };

  const entryCount = stats?.total_entries ?? 0;

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse">Loading registry configuration...</div>
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="p-4">
        <div className="text-red-600" role="alert">Error loading registry configuration</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Sub-tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700 px-4">
        <nav className="flex space-x-6" aria-label="Registry sub-tabs">
          <button
            onClick={() => setActiveSubTab('configuration')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeSubTab === 'configuration'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
            }`}
          >
            Configuration
          </button>
          <button
            onClick={() => setActiveSubTab('entries')}
            disabled={!enabled}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
              activeSubTab === 'entries'
                ? 'border-primary-500 text-primary-600'
                : enabled
                  ? 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
                  : 'border-transparent text-gray-300 cursor-not-allowed'
            }`}
          >
            Entries
            {enabled && (
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                activeSubTab === 'entries'
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-primary-600 dark:text-primary-400'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {entryCount}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Sub-tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeSubTab === 'configuration' ? (
          /* Configuration Tab Content */
          <div className="p-4 space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded" role="alert">
                {error}
              </div>
            )}

            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded" role="status">
                {successMessage}
              </div>
            )}

            {/* Enable Toggle */}
            <div className="flex items-center space-x-3">
              <label htmlFor="enable-registry" className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  id="enable-registry"
                  role="checkbox"
                  aria-label="Enable Registry"
                  checked={enabled}
                  onChange={handleToggleEnabled}
                  className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Registry</span>
              </label>
            </div>

            {/* Dedup Configuration - only shown when registry is enabled */}
            {enabled && (
              <>
                {/* Dedup Mode Selection */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Deduplication Key Mode</h3>

                  <div className="space-y-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="dedup-mode"
                        value="entire"
                        checked={dedupMode === 'entire'}
                        onChange={() => handleDedupModeChange('entire')}
                        className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Entire object</span>
                    </label>

                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="dedup-mode"
                        value="specific"
                        checked={dedupMode === 'specific'}
                        onChange={() => handleDedupModeChange('specific')}
                        className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Specific fields</span>
                    </label>
                  </div>
                </div>

                {/* Field Selection Checkboxes */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {dedupMode === 'entire' ? 'Model Fields (all included)' : 'Select Fields for Dedup Key'}
                  </h3>
                  <div className={`space-y-2 ml-4 ${dedupMode === 'entire' ? 'opacity-50' : ''}`}>
                    {modelFields.map(field => (
                      <label key={field} className={`flex items-center ${dedupMode === 'entire' ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={dedupMode === 'entire' ? true : selectedFields.includes(field)}
                          onChange={() => handleFieldToggle(field)}
                          disabled={dedupMode === 'entire'}
                          className={`mr-2 h-4 w-4 border-gray-300 dark:border-gray-600 rounded ${
                            dedupMode === 'entire'
                              ? 'text-gray-400 cursor-not-allowed'
                              : 'text-primary-600 focus:ring-primary-500'
                          }`}
                        />
                        <span className={`text-sm ${dedupMode === 'entire' ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}>{field}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Preview Section */}
                <div className="space-y-2">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Preview</h3>
                  <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-3">
                    <code className="text-sm text-gray-800 dark:text-gray-200 break-all">
                      {buildPreviewText()}
                    </code>
                  </div>
                </div>
              </>
            )}

            {/* Save Button */}
            <div className="pt-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className={`px-4 py-2 rounded-md text-white font-medium transition-colors ${
                  saving
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500'
                }`}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          /* Entries Tab Content */
          <div className="p-4">
            {enabled ? (
              <RegistryEntriesList model={model} onEntriesChanged={() => { loadData(); onRegistryChange?.(); }} />
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Enable the registry to view entries.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelRegistryTab;
